// Convert Firestore documents into the plain Live Map model
import { compact } from 'lodash';

import { getJobStartDatetime } from '@isd/lib/firebase/db/helpers';
import { JobAssignmentDoc, JobDoc } from '@isd/lib/firebase/db/metaTypes';

import {
  GeotrackPoint,
  LiveMapJob,
  LiveMapOperator,
  ReceiverStatus,
  getJobColor,
  getJobSites,
} from '@/components/LiveMap/liveMapModel';

// Fields present on the documents but not (yet) part of the `@isd/lib` types
type JobAssignmentExtraFields = {
  receiver?: {
    status?: string;
    fakeStatus?: string;
    user?: { truck?: { truckDisplayId?: string } };
  };
  task?: { truckDisplayId?: string };
};
type JobExtraFields = {
  sites?: Record<
    string,
    {
      lat?: number;
      lng?: number;
      order?: number;
      name?: string;
      address?: string;
      radius?: number;
      innerRadius?: number;
      polygonGeofence?: Array<{ lat?: number; lng?: number }> | null;
    }
  >;
};

// react-app has more receiver statuses than the @isd/lib type lists; fold the
// "still waiting on an answer" ones into 'notified'.
const KNOWN_STATUSES: ReceiverStatus[] = [
  'not notified',
  'notified',
  'accepted',
  'acceptedNotCheckedIn',
  'declined',
  'checkedIn',
  'checkedOut',
];

/**
 * Normalize a raw `receiver.status` value.
 * @param {string | undefined} status
 * @returns {ReceiverStatus}
 */
export function normalizeReceiverStatus(
  status: string | undefined
): ReceiverStatus {
  if (!status) return 'not notified';
  return KNOWN_STATUSES.includes(status as ReceiverStatus)
    ? (status as ReceiverStatus)
    : 'notified';
}

/**
 * Map a Job document to the Live Map job model.
 * @param {JobDoc} jobDoc
 * @param {number} index - Position in the scheduler's job list, used to pick a color.
 * @returns {LiveMapJob}
 */
export function toLiveMapJob(jobDoc: JobDoc, index: number): LiveMapJob {
  const data = jobDoc.data() as JobExtraFields | undefined;
  return {
    id: jobDoc.id,
    name: jobDoc.get('name') || 'Untitled job',
    clientName: jobDoc.get('clientName') || '',
    startDatetime: getJobStartDatetime(jobDoc),
    totalTrucks: jobDoc.get('totalTrucks') || 0,
    color: getJobColor(index),
    sites: getJobSites(data?.sites),
  };
}

/**
 * Group the leaf JobAssignments of today's jobs by operator. An operator may be
 * assigned to more than one job in a day.
 * @param {JobAssignmentDoc[]} assignmentDocs - Leaf-node assignments across jobs.
 * @returns {LiveMapOperator[]}
 */
export function toLiveMapOperators(
  assignmentDocs: JobAssignmentDoc[]
): LiveMapOperator[] {
  const operatorsByUid: Record<string, LiveMapOperator> = {};
  assignmentDocs.forEach((assignmentDoc) => {
    const extra = assignmentDoc.data() as JobAssignmentExtraFields | undefined;
    // Fake statuses are used by react-app for demo/test assignments
    if (!!extra?.receiver?.fakeStatus) return;
    const uid = assignmentDoc.get('receiver.user.id');
    if (!uid) return;
    const operator = (operatorsByUid[uid] = operatorsByUid[uid] || {
      uid,
      name:
        compact([
          assignmentDoc.get('receiver.user.firstName'),
          assignmentDoc.get('receiver.user.lastName'),
        ]).join(' ') ||
        assignmentDoc.get('receiver.user.nickname') ||
        assignmentDoc.get('receiver.user.email') ||
        'Unknown operator',
      nickname: assignmentDoc.get('receiver.user.nickname') || '',
      companyName: assignmentDoc.get('receiver.company.name') || '',
      truckDisplayId: null,
      assignments: [],
      isSimulated: false,
    });
    operator.truckDisplayId =
      operator.truckDisplayId ||
      extra?.receiver?.user?.truck?.truckDisplayId ||
      extra?.task?.truckDisplayId ||
      null;
    operator.assignments.push({
      assignmentId: assignmentDoc.id,
      jobId: assignmentDoc.get('job.id') || '',
      status: normalizeReceiverStatus(extra?.receiver?.status),
      taskId: assignmentDoc.get('task.id') || null,
    });
  });
  return Object.values(operatorsByUid);
}

type GeotrackPointSource = {
  latitude?: number;
  longitude?: number;
  timestamp?: number;
  heading?: number;
  isMoving?: boolean;
};

/**
 * Map raw Geotrack fields to a point. Returns null for malformed fixes.
 * @param {GeotrackPointSource | undefined} data - Geotrack document data.
 * @returns {GeotrackPoint | null}
 */
export function toGeotrackPoint(
  data: GeotrackPointSource | undefined
): GeotrackPoint | null {
  if (
    !data ||
    !Number.isFinite(data.latitude) ||
    !Number.isFinite(data.longitude) ||
    !Number.isFinite(data.timestamp)
  ) {
    return null;
  }
  return {
    lat: data.latitude as number,
    lng: data.longitude as number,
    timestamp: data.timestamp as number,
    heading:
      Number.isFinite(data.heading) && (data.heading as number) >= 0
        ? (data.heading as number)
        : null,
    isMoving: !!data.isMoving,
  };
}
